/**
 * Delivery Date Calculator
 *
 * Business Rules:
 * 1. Minimum 3 business days between order date and delivery date
 * 2. Sundays are NOT business days (excluded)
 * 3. Holidays from Firestore are excluded
 * 4. Date picker should disable invalid dates
 */

/**
 * Check if a date is Sunday
 * @param {Date} date
 * @returns {boolean}
 */
export function isSunday(date) {
  return date.getDay() === 0;
}

/**
 * Check if a date is a holiday
 * @param {Date} date
 * @param {Array<string>} holidays - Array of date strings in YYYY-MM-DD format
 * @returns {boolean}
 */
export function isHoliday(date, holidays = []) {
  if (!holidays || holidays.length === 0) return false;
  const dateString = formatDateForComparison(date);
  return holidays.includes(dateString);
}

/**
 * Check if a date is a business day (not Sunday, not holiday)
 * @param {Date} date
 * @param {Array<string>} holidays
 * @returns {boolean}
 */
export function isBusinessDay(date, holidays = []) {
  return !isSunday(date) && !isHoliday(date, holidays);
}

/**
 * Format date as YYYY-MM-DD for comparison
 * @param {Date} date
 * @returns {string}
 */
export function formatDateForComparison(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Add business days to a date (skipping Sundays and holidays)
 * @param {Date} startDate
 * @param {number} businessDays - Number of business days to add
 * @param {Array<string>} holidays
 * @returns {Date}
 */
export function addBusinessDays(startDate, businessDays, holidays = []) {
  let currentDate = new Date(startDate);
  let daysAdded = 0;

  while (daysAdded < businessDays) {
    currentDate.setDate(currentDate.getDate() + 1);

    if (isBusinessDay(currentDate, holidays)) {
      daysAdded++;
    }
  }

  return currentDate;
}

/**
 * Calculate minimum delivery date (3 business days from today)
 * @param {Date} orderDate - The date when order is placed (defaults to today)
 * @param {Array<string>} holidays
 * @returns {Date}
 */
export function calculateMinimumDeliveryDate(orderDate = new Date(), holidays = []) {
  return addBusinessDays(orderDate, 3, holidays);
}

/**
 * Check if a delivery date is valid (at least 3 business days from order date)
 * @param {Date} deliveryDate
 * @param {Date} orderDate
 * @param {Array<string>} holidays
 * @returns {boolean}
 */
export function isValidDeliveryDate(deliveryDate, orderDate = new Date(), holidays = []) {
  // Reset times to start of day for accurate comparison
  const delivery = new Date(deliveryDate);
  delivery.setHours(0, 0, 0, 0);

  const order = new Date(orderDate);
  order.setHours(0, 0, 0, 0);

  // Delivery date must be after order date
  if (delivery <= order) {
    return false;
  }

  // Delivery date must not be Sunday or holiday
  if (!isBusinessDay(delivery, holidays)) {
    return false;
  }

  // Calculate minimum date
  const minDate = calculateMinimumDeliveryDate(order, holidays);
  minDate.setHours(0, 0, 0, 0);

  // Delivery date must be >= minimum date
  return delivery >= minDate;
}

/**
 * Generate array of disabled dates for date picker
 * @param {Date} startDate - Start date (usually today)
 * @param {number} daysAhead - How many days to generate (default 90)
 * @param {Array<string>} holidays
 * @returns {Array<string>} Array of disabled dates in YYYY-MM-DD format
 */
export function getDisabledDates(startDate = new Date(), daysAhead = 90, holidays = []) {
  const minDeliveryDate = calculateMinimumDeliveryDate(startDate, holidays);
  const disabledDates = [];

  for (let i = 0; i < daysAhead; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    date.setHours(0, 0, 0, 0);

    // Disable if:
    // 1. Before minimum delivery date
    // 2. Is Sunday
    // 3. Is holiday
    if (date < minDeliveryDate || !isBusinessDay(date, holidays)) {
      disabledDates.push(formatDateForComparison(date));
    }
  }

  return disabledDates;
}

/**
 * Get next available delivery date
 * @param {Date} fromDate
 * @param {Array<string>} holidays
 * @returns {Date}
 */
export function getNextAvailableDate(fromDate = new Date(), holidays = []) {
  let date = new Date(fromDate);
  date.setHours(0, 0, 0, 0);

  // Keep adding days until we find a valid business day
  while (!isBusinessDay(date, holidays)) {
    date.setDate(date.getDate() + 1);
  }

  return date;
}

/**
 * Format date for display (e.g., "Monday, Jan 15, 2025")
 * @param {Date} date
 * @returns {string}
 */
export function formatDateForDisplay(date) {
  const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Count business days between two dates
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {Array<string>} holidays
 * @returns {number}
 */
export function countBusinessDays(startDate, endDate, holidays = []) {
  let count = 0;
  let currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  while (currentDate < end) {
    currentDate.setDate(currentDate.getDate() + 1);
    if (isBusinessDay(currentDate, holidays)) {
      count++;
    }
  }

  return count;
}
